import { useCallback, useMemo } from "react";
import styled from "styled-components";
import { Campaign } from "../../../ducks/campaign";
import { BASIC_DARK_GRAY, BASIC_BROWN } from "../../../constants/colors";
import { Store } from "../../../ducks/store";
import { useEarlyBird } from "../../../hooks/useEarlyBird";
import {
  getCurrentDateUTC,
  getUTCDateByHourMinute,
  getUTCDatePlusMinutes,
  isSameDate,
} from "../../../utils/date";
import { StoreCard } from "../../molecules/store/StoreCard";

type Props = {
  stores: Store[];
  pickupDate: string;
  selectedDate?: string;
  hiddenShortestTime?: boolean;
  onSelectStore: (storeCode: string, pickupDate: string) => void;
  onFavoriteStore: (
    storeId: number,
    favorite: boolean,
    onSuccess?: () => void,
  ) => void;
  campaign: Campaign | null;
};

export const StoreList: React.FC<Props> = ({
  stores,
  pickupDate,
  selectedDate,
  hiddenShortestTime,
  onSelectStore,
  onFavoriteStore,
  campaign,
}) => {
  const handleSelectShortestPickUpTime = useCallback(
    (storeCode: string, shortestPickUpTime: number) => {
      const currentData = getCurrentDateUTC();
      onSelectStore(
        storeCode,
        getUTCDatePlusMinutes(new Date(currentData), shortestPickUpTime),
      );
    },
    [pickupDate],
  );

  const { checkCampaignStatus } = useEarlyBird();

  const handleSelectByTime = useCallback(
    (storeCode: string, hour: number, minute: number) => {
      checkCampaignStatus({
        onOk: () => {
          onSelectStore(
            storeCode,
            getUTCDateByHourMinute(new Date(pickupDate), hour, minute),
          );
        },
      });
    },
    [pickupDate],
  );

  const isSameDayPickUp = useMemo(() => {
    return isSameDate(new Date(), new Date(pickupDate));
  }, [pickupDate]);

  return (
    <>
      {stores.map((store) => (
        <StoreListContainer key={store.id} id={`store-${store.id}`}>
          <StoreCard
            id={store.id}
            isSameDayPickUp={isSameDayPickUp}
            name={store.name}
            address={store.prefecture + store.city + store.address}
            phoneNumber={store.phoneNumber}
            service={store.service}
            location={{
              lat: store.location.latitude,
              lng: store.location.longitude,
            }}
            isAdvancedPayment={store.isPaymentAvailable}
            is24Hours={store.is24Hours}
            isOvernight={store.isOvernight}
            isOrderStopped={store.isOrderStopped}
            isFavorite={store.isFavorite}
            onFavoriteClick={(...args) => onFavoriteStore(store.id, ...args)}
            shortestPickUpTime={store.shortestTimeUntilPickup}
            onSelectShortestPickUpTime={() =>
              handleSelectShortestPickUpTime(
                store.storeCode,
                store.shortestTimeUntilPickup,
              )
            }
            businessHourOpen={store.businessHourOpen}
            businessHourClose={store.businessHourClose}
            onSelectByTime={(hour, minute) =>
              handleSelectByTime(store.storeCode, hour, minute)
            }
            selectedDate={selectedDate}
            pickupDate={pickupDate}
            hiddenShortestTime={hiddenShortestTime}
            campaign={campaign}
          />
        </StoreListContainer>
      ))}
    </>
  );
};

const StoreListContainer = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${BASIC_DARK_GRAY};
`;
